package ui.campaign.detailv2.editor.feature

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import api.CampaignType
import api.DayOfWeek
import common.frontend.api.Io
import common.frontend.api.Io.Campaign
import kotlinx.datetime.*
import org.ssoft.common.tools.nowDate
import org.ssoft.common.tools.nowDateTime
import theme.*
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperELocation
import kotlin.math.roundToLong
import kotlin.time.Clock
import kotlin.time.Duration.Companion.hours

abstract class FeatureCounter(val config: Campaign) {


    protected val oneH = 1.hours.inWholeMilliseconds.toDouble()
    lateinit var locationOH: Map<Long, LocationTimeOH>
    protected lateinit var states: Io.ListingCampaignDevicesCheck

    var isLoading by mutableStateOf(true)
        private set

    class LocationTimeOH(
        val locationId: Long,
        val daySpecsByIsoWeekDay: Map<Int, Day> = mapOf()

    ) {
        data class Day(
            val dayOfWeek: DayOfWeek,
            val ranges: List<Pair<LocalTime, LocalTime>>,
            val durationMs: Long,
            val enabled: Boolean = ranges.isNotEmpty()
        )
    }


    class DayInfo(
        val date: LocalDate,
        val impressionSum: Long,
        val usageNow: Float,
        val usageFuture: Float,
        val canBeUsed: Boolean,
        val inCampaignRange: Boolean,
    )

    companion object {
        fun computeColor(pct: Float) = when {
            pct < 20F -> ColorMaterialGreen500
            pct < 60F -> ColorMaterialBlue500
            pct < 90F -> ColorMaterialViolet500
            pct <= 100F -> ColorMaterialOrange500
            else -> ColorMaterialRed500
        }

        suspend fun get(companyId: Long, config: Campaign): FeatureCounter {

            val counter = when (CampaignType[config.settings.type]) {
                CampaignType.IMPRESSION -> FeatureCounterImpression(config)
                CampaignType.SLOT -> FeatureCounterSlot(config)
                CampaignType.NONE -> error("WTF campaign type of none")
            }
            counter.loadStates(companyId)
            return counter
        }
    }

    init {
        calculateLocationTimes()
    }

    private fun calculateLocationTimes() {
        locationOH = config.locations.map { location ->
            val days = DayOfWeek.all.map { dow ->
                location.days.firstOrNull { it.day == dow.index }
                    ?.let { dayActual ->
                        val dayRanges = mutableListOf<Pair<LocalTime, LocalTime>>()

                        val dayBefore = location.days.firstOrNull { it.day == dow.getDayOfYesterday().index }
                        if (dayBefore != null && dayBefore.start > dayBefore.end) {
                            dayRanges += LocalTime(0, 0) to dayBefore.end
                        }
                        dayRanges += if (dayActual.start < dayActual.end) dayActual.start to dayActual.end
                        else dayActual.start to LocalTime(23, 59, 59)

                        LocationTimeOH.Day(
                            dayOfWeek = dow,
                            ranges = dayRanges,
                            durationMs = dayRanges.sumOf { it.second.toMillisecondOfDay() - it.first.toMillisecondOfDay() }.toLong()
                        )
                    } ?: LocationTimeOH.Day(
                    dayOfWeek = dow,
                    ranges = listOf(),
                    durationMs = 0
                )
            }.associateBy { it.dayOfWeek.isoDay }

            LocationTimeOH(
                locationId = location.id,
                daySpecsByIsoWeekDay = days
            )
        }.associateBy { it.locationId }
    }

    suspend fun compute(wrapper: CampaignWrapperELocation) {
        isLoading = true
        runCatching {
            val today = Clock.nowDate()
            val dateYearThis = LocalDate(year = today.year, month = 1, day = 1)
            val dateYearNext = LocalDate(year = today.year + 1, month = 1, day = 1)

            wrapper.locations.state.forEach { location ->
                location.ohSpecs = locationOH[location.data.id]!!
                location.devices.state.forEach { device ->
                    device.usageByDoy.clear()
                    computeAlreadyUsed(
                        device = device,
                        states = states,
                        today = today,
                        ohSpecs = location.ohSpecs,
                        dateYearThis = dateYearThis,
                        dateYearNext = dateYearNext
                    )
                }
            }
            var current = wrapper.data.start.date
            val end = wrapper.data.end.date

            val emptyDoyEntry = Io.ListingCampaignDevicesCheck.Entry(
                deviceId = 0,
                doy = 0,
                sum = 0
            )
            while (current <= end) {
                wrapper.locations.state
                    .filter { it.ohSpecs.daySpecsByIsoWeekDay[current.dayOfWeek.isoDayNumber]?.enabled == true }
                    .forEach { location ->
//                        println(location.ohSpecs)
                        val daySpecs = location.ohSpecs.daySpecsByIsoWeekDay[current.dayOfWeek.isoDayNumber]!!
                        location.devices.state
                            .filterNot { it.usageByDoy.contains(current.dayOfYear) }
                            .forEach { device ->
                                device.usageByDoy[current.dayOfYear] = calculateUsage(
                                    date = current,
                                    doyEntry = emptyDoyEntry,
                                    daySpace = daySpecs.durationMs,
                                    companyRepeats = config.settings.companyCount.toLong(),
                                    companyDuration = config.settings.companyDuration.toLong(),
                                    campaignRepeats = config.settings.campaignCount.toLong(),
                                    campaignDuration = config.settings.campaignDuration,
                                )
                            }
                    }
                current = current.plus(1, DateTimeUnit.DAY)
            }
            makeSummary(wrapper)
        }.onFailure {
            it.printStackTrace()
        }
        isLoading = false
    }

    private fun FeatureCounter.computeAlreadyUsed(
        device: CampaignWrapperELocation.AdapterDevice.Item,
        states: Io.ListingCampaignDevicesCheck,
        today: LocalDate,
        dateYearThis: LocalDate,
        dateYearNext: LocalDate,
        ohSpecs: LocationTimeOH,
    ) {

        val usedDays = states.items.filter { it.deviceId == device.data.id }
        device.usageByDoy = usedDays.mapNotNull { doy ->
            val date = if (today.dayOfYear <= doy.doy) {
                // this year
                if (doy.doy == 1L) dateYearThis
                else dateYearThis.plus(doy.doy - 1, DateTimeUnit.DAY)
            } else {
                //future Year
                if (doy.doy == 1L) dateYearNext
                else dateYearNext.plus(doy.doy - 1, DateTimeUnit.DAY)
            }
            ohSpecs.daySpecsByIsoWeekDay[date.dayOfWeek.isoDayNumber]
                ?.takeIf { it.enabled }
                ?.let { weekDaySpecs ->
                    if (date >= config.start.date && date <= config.end.date) {
                        calculateUsage(
                            date = date,
                            doyEntry = doy,
                            daySpace = weekDaySpecs.durationMs,
                            companyRepeats = config.settings.companyCount.toLong(),
                            companyDuration = config.settings.companyDuration.toLong(),
                            campaignRepeats = config.settings.campaignCount.toLong(),
                            campaignDuration = config.settings.campaignDuration,
                        )
                    } else {
                        calculateUsage(
                            date = date,
                            doyEntry = doy,
                            daySpace = weekDaySpecs.durationMs,
                            companyRepeats = config.settings.companyCount.toLong(),
                            companyDuration = config.settings.companyDuration.toLong(),
                            campaignRepeats = 0L,
                            campaignDuration = 0L,
                        )
                    }
                }
        }.associateBy { it.date.dayOfYear }.toMutableMap()
    }

    private fun makeSummary(wrapper: CampaignWrapperELocation) {
        val now = Clock.nowDateTime()
        wrapper.locations.state.forEach { location ->
            location.devices.state.forEach { device ->
                device.usageByDoy.values
                    .filter { it.inCampaignRange }
                    .apply {


                        val daysWhichCanBeUsed = filter { it.canBeUsed }
                        val daysWhichCanNotBeUsed = filterNot { it.canBeUsed }

                        device.countAllDays = count()
                        device.countUsableDays = daysWhichCanBeUsed.count()
                        device.countNotUsableDays = daysWhichCanNotBeUsed.count()

                        // plnenie cielov kampane %
                        device.goalAccomplishment = (device.countUsableDays.toFloat() / device.countAllDays.toFloat()) * 100F

                        runCatching {
                            // decrease impressions of today
                            daysWhichCanBeUsed.firstOrNull { it.date == now.date }?.also { today ->
                                var secTotal = 0L
                                var secUsable = 0L
                                locationOH[location.data.id]?.daySpecsByIsoWeekDay[today.date.dayOfWeek.isoDayNumber]?.ranges?.forEach {
                                    secTotal += it.second.toSecondOfDay() - it.first.toSecondOfDay()
                                    if (now.time in it.first..it.second) {
                                        secUsable += it.second.toSecondOfDay() - now.time.toSecondOfDay()
                                    }
                                }
                                secUsable -= 1.hours.inWholeSeconds

                                val impressionsForToday = runCatching {
                                    if (secUsable <= 0) 0
                                    else ((secUsable.toDouble() / secTotal.toDouble()) * today.impressionSum).roundToLong()
                                }.getOrNull() ?: 0L

                                if (impressionsForToday > 0) {
                                    device.impressionSum = impressionsForToday
                                } else {
                                    device.countUsableDays -= 1
                                    device.countNotUsableDays += 1
                                }
                                device.impressionSum += (daysWhichCanBeUsed - today).sumOf { it.impressionSum }
                            } ?: run {
                                device.impressionSum = daysWhichCanBeUsed.sumOf { it.impressionSum }
                            }
                        }.onFailure {
                            device.impressionSum = daysWhichCanBeUsed.sumOf { it.impressionSum }
                        }

                        // dni v ktorych sa neda prehravat
                        device.setExcludedDays(daysWhichCanNotBeUsed.map { it.date.dayOfYear })

                        // maximalne vyuzitie zariadenia pred aplikovanim kampane
//                        val maxUsageNow = maxOf { it.usageNow }

                        // maximalne vyuzitie zariadenia po aplikovani kampane
                        device.maxUsageFuture = maxOfOrNull { it.usageFuture }?.let { it * 100F } ?: 0F
                        device.usageNow = maxOfOrNull { it.usageNow }?.let { it * 100F } ?: 0F

//                        val averageUsageNow = sumOf { it.usageNow.toDouble() } / countAllDays.toDouble()
//                        val averageUsageFuture = daysWhichCanBeUsed.sumOf { it.usageFuture.toDouble() } / countUsableDays.toDouble()


                    }
            }
        }
    }

    protected abstract fun calculateUsage(
        date: LocalDate,
        doyEntry: Io.ListingCampaignDevicesCheck.Entry,
        daySpace: Long,
        companyRepeats: Long,
        companyDuration: Long,
        campaignRepeats: Long,
        campaignDuration: Long
    ): DayInfo

    private suspend fun loadStates(companyId: Long) {
        if (config.isCommercial) {
            states = loadStatesInternal(companyId, config.id)
        } else {
            states = Io.ListingCampaignDevicesCheck()
        }
    }

    protected abstract suspend fun loadStatesInternal(companyId: Long, campaignId: Long): Io.ListingCampaignDevicesCheck
}

